import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCardCheckoutApi,
  getCheckoutPaymentSession,
} from "../store/slices/cardApisSlice";
import { getCancelOrder } from "../store/slices/orderDetailsSlice";

// The SDK is loaded by the deferred <script id="checkout-script"> in
// public/index.html, so it may not have executed by the time this hook runs.
const loadFlowSdk = () =>
  new Promise((resolve, reject) => {
    if (window.CheckoutWebComponents) {
      resolve(window.CheckoutWebComponents);
      return;
    }
    const script = document.getElementById("checkout-script");
    if (!script) {
      reject(new Error("Checkout Flow SDK script tag missing from index.html"));
      return;
    }
    script.addEventListener(
      "load",
      () =>
        window.CheckoutWebComponents
          ? resolve(window.CheckoutWebComponents)
          : reject(new Error("Checkout Flow SDK loaded without a global")),
      { once: true }
    );
    script.addEventListener(
      "error",
      () => reject(new Error("Checkout Flow SDK failed to load")),
      { once: true }
    );
  });

// Backend reports "staging"/"production"; Flow only accepts "sandbox"/"production".
const isProductionEnvironment = (environment) =>
  environment === "production" || environment === "live";

// Flow wants a BCP-47 tag; the route only carries "en" / "ar".
const toFlowLocale = (langSlug) => (langSlug === "ar" ? "ar" : "en-GB");

// OmanNet cards cannot be paid through the Flow session, so they are paid with
// the card token via checkout-pay (backend /payments), as with Frames.
// OmanNet cards are co-branded (e.g. `scheme: "Visa"`), so Checkout.com
// identifies them by `scheme_local: "omannet"`.
const isOmanNetCard = (data) =>
  /omannet/i.test(data?.scheme_local || "") ||
  /omannet/i.test(data?.scheme || "");

export const useCardsCheckout = ({ id }) => {
  const [payButtonDisabled, setPayButtonDisabled] = useState(true);
  const [loadingShimmer, setLoadingShimmer] = useState(false);
  const [errorMsgCard, setErrorMsgCard] = useState("");
  const [flowReady, setFlowReady] = useState(false);

  const cardRef = useRef(null);
  // Set while an OmanNet token payment runs, so the error Flow raises for the
  // `continue: false` we return is not shown to the customer.
  const omanNetPaymentRef = useRef(false);

  const { t } = useTranslation(["common"]);

  const dispatch = useDispatch();
  const { slug, lang: langSlug } = useParams();
  const navigate = useNavigate();

  let langString = langSlug;

  const cancelButton = async () => {
    const resultAction = await dispatch(getCancelOrder(slug));

    if (getCancelOrder.fulfilled.match(resultAction)) {
      const lang = localStorage.getItem("language_type");
      if (lang === "ar") {
        navigate("/ar");
      } else if (lang === "en") {
        navigate("/en");
      } else {
        navigate("/en");
      }
    }
  };

  useEffect(() => {
    if (!langSlug) {
      const lang = localStorage.getItem("language_type");
      if (lang === "ar") {
        navigate("/ar");
      } else if (lang === "en") {
        navigate("/en");
      } else {
        navigate("/en");
      }
    }
  }, []);

  localStorage.setItem("id", id);

  const showError = useCallback(() => {
    setErrorMsgCard(t("Something_wrong"));
    setLoadingShimmer(false);
    setTimeout(() => setErrorMsgCard(""), 4000);
  }, [t]);

  // Order details load asynchronously, so wait for the real id before opening
  // a payment session. String() keeps the effect key stable.
  const orderId = id ? String(id) : "";

  useEffect(() => {
    if (!orderId) return;

    let cancelled = false;

    setFlowReady(false);
    setPayButtonDisabled(true);

    const payWithToken = async (data) => {
      try {
        const resultAction = await dispatch(
          getCardCheckoutApi({
            token: data?.token,
            card_type: data?.card_type,
            issuer_country: data?.issuer_country,
            scheme: data?.scheme,
            card_no: data?.last4,
            order_id: orderId,
            app_token: localStorage.getItem("authToken"),
          })
        );

        let responseWith3D = resultAction?.payload?.data?.data?.status;
        let responseWithOut3D = resultAction?.payload?.data?.data?.approved;

        if (responseWith3D == "Pending") {
          setLoadingShimmer(false);
          window.location.href =
            resultAction?.payload?.data?.data?._links?.redirect?.href;
        } else if (responseWithOut3D == true) {
          setLoadingShimmer(false);
          navigate(`/order-summary/${orderId}/success/${langString}`);
        } else if (responseWithOut3D == false) {
          setLoadingShimmer(false);
          navigate(`/order-summary/${orderId}/failed/${langString}`);
        } else {
          showError();
        }
      } catch (error) {
        showError();
      } finally {
        omanNetPaymentRef.current = false;
      }
    };

    const mountFlow = async () => {
      try {
        const CheckoutWebComponents = await loadFlowSdk();

        const response = await dispatch(
          getCheckoutPaymentSession({
            order_id: orderId,
            app_token: localStorage.getItem("authToken"),
          })
        ).unwrap();

        if (cancelled) return;

        const payload = response?.data;
        // The backend returns the session fields (id, payment_session_token,
        // payment_session_secret) directly under `data`.
        const paymentSession = payload?.payment_session ?? payload;

        if (
          response?.success !== true ||
          !paymentSession?.id ||
          !paymentSession?.payment_session_secret
        ) {
          throw new Error(
            `checkout-payment-session returned no usable session: ${response?.message}`
          );
        }

        // Prefer key and environment from the response when the backend sends
        // them; otherwise use .env. Either way they must belong to the same
        // Checkout.com account as the secret key that created the session.
        const isProduction = payload.environment
          ? isProductionEnvironment(payload.environment)
          : process.env.REACT_APP_PRODUCTION === "true";
        const publicKey =
          payload.public_key ||
          (isProduction
            ? process.env.REACT_APP_CHECKOUT_PUBLIC_KEY
            : process.env.REACT_APP_CHECKOUT_TEST_PUBLIC_KEY);

        const checkout = await CheckoutWebComponents({
          publicKey,
          environment: isProduction ? "production" : "sandbox",
          locale: toFlowLocale(langString),
          paymentSession,
          onReady: () => {
            if (!cancelled) setFlowReady(true);
          },
          onChange: (component) => {
            if (!cancelled) setPayButtonDisabled(!component.isValid());
          },
          onTokenized: (_self, tokenizeResult) => {
            if (isOmanNetCard(tokenizeResult?.data)) {
              omanNetPaymentRef.current = true;
              payWithToken(tokenizeResult.data);
              return { continue: false, errorMessage: " " };
            }
            return { continue: true };
          },
          onPaymentCompleted: () => {
            // Flow also redirects to the session's success_url; this covers the
            // non-redirect path.
            setLoadingShimmer(false);
            navigate(`/order-summary/${orderId}/success/${langString}`);
          },
          onError: (_self, error) => {
            if (omanNetPaymentRef.current) return;
            console.log("Flow error: ", error);
            showError();
          },
        });

        if (cancelled) return;

        // Flow's own pay button is hidden so the existing CANCEL / PAY buttons
        // stay; PAY calls submit(), which still runs onTokenized.
        const component = checkout.create("card", { showPayButton: false });
        // isAvailable() is mandatory before mount().
        if (!(await component.isAvailable())) {
          throw new Error("Card payment is not available for this session");
        }
        if (cancelled) return;
        await component.mount("#card-container");
        cardRef.current = component;
      } catch (error) {
        if (cancelled) return;
        console.log("Flow mount error: ", error);
        showError();
      }
    };

    mountFlow();

    return () => {
      cancelled = true;
      try {
        cardRef.current?.unmount();
      } catch (error) {
        console.log("Flow unmount error: ", error);
      }
      cardRef.current = null;
    };
  }, [orderId, langString]);

  const submitPayment = () => {
    if (!cardRef.current) return;
    setLoadingShimmer(true);
    cardRef.current.submit();
  };

  return {
    payButtonDisabled: payButtonDisabled || !flowReady,
    submitPayment,
    flowReady,
    cancelButton,
    loadingShimmer,
    setLoadingShimmer,
    errorMsgCard,
  };
};
